import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import DateInput from '../common/DateInput';
import { InputNumber } from 'primereact/inputnumber';
import { BlockUI } from 'primereact/blockui';
import DriverFilters, { useDriverList } from '../common/DriverFilters';
import ActivityDetailsGrid from '../common/ActivityDetailsGrid';
import ImportResultDialog from './ImportResultDialog';
import AutoBreakDialog from './AutoBreakDialog';
import { driversApi } from '../../api/services';
import { useAlerts } from '../../custom/Alerts';
import { DAY_MS, fmtDate, fmtDuration, startOfToday } from '../../utils/dates';
import PageHeader from '../common/PageHeader';
import { emptyDaily, fillActivitiesWithBreak, firstAvailableDate, normalizeDetail, shiftDetails, sortDetails, sumDurations } from '../../utils/activities';

const dur = (ms) => (ms === DAY_MS ? '24:00' : fmtDuration(ms));

const TOTAL_ROWS = [
    ['Vožnja - ukupno (hh:mm)', 'totalDrivingTime'],
    ['Noćna vožnja (hh:mm)', 'totalNightDrivingTime'],
    ['Ostali rad - ukupno (hh:mm)', 'totalWorkTime'],
    ['Noćni ostali rad (hh:mm)', 'totalNightWorkTime'],
    ['Raspoloživost - ukupno (hh:mm)', 'totalAvailabilityTime'],
    ['Odmor - ukupno (hh:mm)', 'totalBreakTime'],
    ['Nepoznato vreme - ukupno (hh:mm)', 'totalUnknownTime'],
];

/* "Unos aktivnosti": ručni unos jednog dana aktivnosti vozača + uvoz .ddd/.esm fajlova kartica (GEN1, GEN2, GEN2 v2). */
export default function TachoReader({ active }) {
    const alerts = useAlerts();
    const list = useDriverList([]);
    const { driver } = list;
    const [date, setDate] = useState(startOfToday());
    const dateRef = useRef(date);
    dateRef.current = date;
    const [busyDates, setBusyDates] = useState([]);
    const [daily, setDaily] = useState(emptyDaily(startOfToday()));
    const [busy, setBusy] = useState(false);
    const [importState, setImportState] = useState(null); // { files, url }
    const [breakDialog, setBreakDialog] = useState(false);
    const dddRef = useRef(null);

    const loadDates = async (d = driver, from = date) => {
        if (!d) return;
        const dates = await driversApi.activityDates(d.id);
        setBusyDates(dates);
        const free = firstAvailableDate(from, dates);
        moveTo(free);
    };

    /* Promena datuma pomera i već unete detalje na novi dan (isto vreme u danu) — "Dopuni" i izmena vremena
       rade nad datumom dana, pa detalji moraju biti na njemu. */
    const moveTo = (d) => {
        const prev = dateRef.current;
        dateRef.current = d;
        setDate(d);
        setDaily((x) => ({ ...x, activityDate: d, activityDetails: shiftDetails(x.activityDetails, prev, d) }));
    };

    useEffect(() => { if (driver) loadDates(driver, date).catch(() => {}); }, [driver && driver.id]);

    const pickDate = (d) => { if (d) moveTo(d); };

    const recalc = async (details, distance = daily.traversedDistance) => {
        const normalized = sortDetails(details.map((d) => normalizeDetail(d)));
        const draft = { ...daily, activityDate: date, traversedDistance: distance, activityDetails: normalized };
        setDaily(draft);
        try {
            const t = await driversApi.totals(draft);
            setDaily({ ...draft, ...Object.fromEntries(Object.entries(t).filter(([k]) => k.startsWith('total'))) });
        } catch { /* toast */ }
    };

    const total = sumDurations(daily);
    const totalOk = total === DAY_MS;

    const fill = () => recalc(fillActivitiesWithBreak(daily.activityDetails, date));

    const save = async () => {
        setBusy(true);
        try {
            await driversApi.addActivity({ driverId: driver.id, driverActivity: { ...daily, activityDate: date } });
        } catch {
            alerts.error('Dodavanje aktivnosti neuspešno');
            setBusy(false);
            return;
        }
        alerts.success('Aktivnost je uspešno dodata');
        try {
            const dates = await driversApi.activityDates(driver.id);
            setBusyDates(dates);
            const next = firstAvailableDate(date, dates);
            setDate(next);
            setDaily(emptyDaily(next));
        } catch { /* toast prikazuje interceptor */ }
        setBusy(false);
    };

    const onFiles = (url) => (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (files.length) { setBusy(true); setImportState({ files, url }); }
    };

    return (
        <BlockUI blocked={busy}>
            <PageHeader title="Unos aktivnosti" subtitle="Ručni unos jednog dana aktivnosti vozača ili uvoz .ddd fajlova kartica (Gen1, Gen2, Gen2 v2); fajl tahografa vozila beleži očitavanje vozila.">
                <input ref={dddRef} type="file" accept=".ddd,.esm" multiple hidden onChange={onFiles('/tacho')} />
                <Button label="Uvezi .DDD fajl(ove) za vozače" icon="pi pi-upload" disabled={busy} onClick={() => dddRef.current.click()} />
            </PageHeader>

            <div className="panel">
                <DriverFilters list={list} required onRefreshed={(all) => {
                    const d = all.find((x) => driver && x.id === driver.id) || all[0];
                    if (d) loadDates(d, date).catch(() => {});
                }}>
                    <div className="field date">
                        <label htmlFor="tr-date">Datum aktivnosti</label>
                        <DateInput inputId="tr-date" value={date} onChange={(e) => pickDate(e.value)} dateFormat="dd.mm.yy" showIcon monthNavigator yearNavigator
                            yearRange="2010:2040" disabledDates={busyDates} />
                    </div>
                    <div className="filters-actions">
                        <Button label="Generiši odmor" icon="pi pi-calendar-plus" text severity="secondary" tooltip="Generiši odmor aktivnosti za period"
                            tooltipOptions={{ position: 'bottom' }} disabled={busy || !driver} onClick={() => setBreakDialog(true)} />
                    </div>
                </DriverFilters>
            </div>

            <div className="layout-split">
                <section className="panel" aria-labelledby="tr-details">
                    <div className="panel-head">
                        <div>
                            <h3 className="panel-title" id="tr-details">Aktivnosti za {fmtDate(date)}</h3>
                            <p className="panel-sub">Dodajte aktivnosti dugmetom +. Ukupna vremena se preračunavaju posle svake izmene.</p>
                        </div>
                    </div>
                    <div className="grid-box tall">
                        <ActivityDetailsGrid details={daily.activityDetails} day={date} onChange={(l) => recalc(l)}
                            emptyMessage="Još nema aktivnosti za ovaj dan" />
                    </div>
                </section>

                <aside className="panel sticky-side" aria-labelledby="tr-totals">
                    <div className="panel-head"><h3 className="panel-title" id="tr-totals">Ukupno za dan</h3></div>
                    <div className="totals">
                        <div className="totals-row">
                            <label htmlFor="tr-dist">Pređena distanca (m)</label>
                            <InputNumber inputId="tr-dist" value={daily.traversedDistance} onValueChange={(e) => setDaily({ ...daily, traversedDistance: e.value || 0 })}
                                useGrouping={false} min={0} />
                        </div>
                        {TOTAL_ROWS.map(([label, key]) => (
                            <div key={key} className={`totals-row${key.includes('Night') ? ' sub' : ''}`}>
                                <span>{label}</span><span className="val">{dur(daily[key])}</span>
                            </div>
                        ))}
                    </div>
                    <div className={`totals-sum ${totalOk ? 'ok' : 'bad'}`} aria-live="polite">
                        <span className="label">Ukupno (hh:mm)</span>
                        <span className="val">{dur(total)}</span>
                    </div>
                    {!totalOk && <p className="panel-note"><i className="pi pi-info-circle" aria-hidden="true" />Za čuvanje zbir aktivnosti mora biti tačno 24:00.</p>}
                    <div className="panel-foot">
                        <Button label="Dopuni" icon="pi pi-plus-circle" severity="secondary" outlined tooltip="Dopuni aktivnosti u tabeli"
                            tooltipOptions={{ position: 'top' }} disabled={busy || totalOk} onClick={fill} />
                        <Button label="Sačuvaj" icon="pi pi-check" onClick={save}
                            disabled={busy || !driver || !totalOk || daily.traversedDistance < 0} />
                    </div>
                </aside>
            </div>

            {importState && (
                <ImportResultDialog files={importState.files} url={importState.url} onFinished={() => setBusy(false)}
                    onClose={() => { setImportState(null); if (driver) loadDates(driver, date).catch(() => {}); }} />
            )}
            {breakDialog && (
                <AutoBreakDialog driver={driver} date={date} busyDates={busyDates}
                    onClose={() => setBreakDialog(false)} onDone={() => loadDates(driver, date)} />
            )}
        </BlockUI>
    );
}
